// Mealplan template: a cycle of days with breakfast/lunch/dinner recipes.
// ➕ "Apply to planner from…" copies it into the planner (replaces Swift's hidden Week-N plans).
// Edit mode keeps the layout: texts become fields in place, settings controls come alive.
import React, { useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useNav, type DetailParams } from '../navigation';
import { useFormatDay, useDiet, useHeader, useToday } from '../hooks';
import { useLibrary, useRecipeLookup } from '../store/library';
import { usePlanner } from '../store/planner';
import { addDays } from '../logic/dates';
import { applyMealplan, mealplanDay } from '../logic/schedule';
import { fromNumber, toNumber } from '../logic/quantity';
import NutritionSheet from '../components/NutritionSheet';
import Sheet from '../components/Sheet';
import { RecipePickerSheet, SLOTS } from '../components/Pickers';
import { notify, showMenu } from '../components/Dialogs';
import { Caption, Chip, EmptyState, IconButton, ListItem, Row, Screen, Section, SectionHeader, Stepper } from '../components/ui';
import Icon from '../components/Icon';
import { EditableText, Swap } from '../components/Motion';
import { WEEKDAYS } from '../logic/dates';
import { font, spacing, usePalette } from '../theme';
import type { IngredientLine, Mealplan, Recipe, Slot } from '../types';

export function perServingLines(recipes: Recipe[]): IngredientLine[] {
  return recipes.flatMap(r => r.ingredients.map(l => ({ ...l, amount: fromNumber(toNumber(l.amount) / Math.max(r.servings, 1)) })));
}

export default function MealplanScreen({ route }: NativeStackScreenProps<DetailParams, 'Mealplan'>) {
  const formatDay = useFormatDay();
  const { t } = useTranslation();
  const nav = useNav();
  const p = usePalette();
  const today = useToday();
  const plan = useLibrary(s => s.mealplans[route.params.id]);
  const put = useLibrary(s => s.put);
  const lookup = useRecipeLookup();
  const diet = useDiet();
  const addEntries = usePlanner(s => s.addEntries);
  const [editing, setEditing] = useState(!!route.params.edit);
  const [pick, setPick] = useState<{ day: number; slot: Slot } | null>(null);
  const [sheet, setSheet] = useState<'nutrition' | 'apply' | null>(null);

  useHeader(
    {
      title: plan?.title ?? '',
      headerRight: () => (
        <Row gap={spacing.m}>
          {!editing ? (
            <IconButton
              icon="more"
              label={t('common.more')}
              onPress={() =>
                showMenu([
                  { label: t('mealplan.applyToPlanner'), icon: 'calendarAdd', onPress: () => setSheet('apply') },
                  { label: t('recipe.nutrition'), icon: 'nutrition', onPress: () => setSheet('nutrition') },
                ])
              }
            />
          ) : null}
          <IconButton icon={editing ? 'check' : 'edit'} label={editing ? t('common.done') : t('common.edit')} onPress={() => setEditing(e => !e)} />
        </Row>
      ),
    },
    [editing, plan?.title, t],
  );

  if (!plan) return <EmptyState icon="mealplan" title={t('common.empty')} />;
  const update = (patch: Partial<Mealplan>) => put('mealplans', { ...plan, ...patch });

  return (
    <Screen>
      <EditableText editing={editing} value={plan.title} onChangeText={v => update({ title: v.replace(/\n/g, ' ') })} accessibilityLabel={t('editors.title')}
        style={{ fontSize: font.title, fontWeight: '700', color: p.text, lineHeight: 34 }} />
      <EditableText editing={editing} value={plan.explainer} onChangeText={v => update({ explainer: v })} placeholder={t('recipe.aboutMealplan')}
        style={{ fontSize: font.body, color: p.text, lineHeight: 22 }} />
      {/* The same controls in both modes; they only respond while editing. */}
      <Row gap={spacing.m}>
        <Caption>{t('mealplan.cycle')}</Caption>
        <Stepper label={t('mealplan.cycle')} value={plan.cycleDays} min={1} max={28} onChange={v => update({ cycleDays: v })} readOnly={!editing} />
      </Row>
      <Row>
        <Caption>{t('mealplan.startsOn')}</Caption>
        {WEEKDAYS.map(d => (
          <Chip key={d} label={t(`weekdays.${d}`)} selected={plan.startWeekday === d} onPress={editing ? () => update({ startWeekday: d }) : undefined} />
        ))}
      </Row>

      {Array.from({ length: plan.cycleDays }, (_, day) => {
        const entries = mealplanDay(plan, day);
        return (
          <Section
            key={day}
            title={t('mealplan.day', { n: day + 1 })}
            right={<IconButton icon="plus" label={t('common.add')} onPress={() => showMenu(SLOTS.map(s => ({ label: t(`planner.${s.slot}`), icon: s.icon, onPress: () => setPick({ day, slot: s.slot }) })))} />}>
            {SLOTS.map(s => {
              const inSlot = entries.filter(e => e.slot === s.slot);
              if (!inSlot.length) return null;
              return (
                <View key={s.slot} style={{ gap: spacing.xs }}>
                  <SectionHeader title={t(`planner.${s.slot}`)} />
                  {inSlot.map((e, i) => {
                    const r = lookup(e.recipeId);
                    return (
                      <ListItem
                        key={`${e.recipeId}-${i}`}
                        title={r?.title ?? e.recipeId}
                        diets={diet.issues(r)}
                        subtitle={r ? `${t('common.servings', { count: r.servings })} · ${t('common.minutes', { count: r.durationMin })}` : undefined}
                        icon={s.icon}
                        onPress={editing ? undefined : () => nav.navigate('Recipe', { id: e.recipeId })}
                        chevron={false}
                        right={
                          <Swap
                            showB={editing}
                            style={{ width: 32, height: 32, alignItems: 'center', justifyContent: 'center' }}
                            a={<Icon name="forward" size={18} color={p.secondaryText} />}
                            b={<IconButton icon="trash" color={p.danger} label={t('common.delete')} onPress={() => update({ entries: plan.entries.filter(x => x !== e) })} />}
                          />
                        }
                      />
                    );
                  })}
                </View>
              );
            })}
          </Section>
        );
      })}

      <RecipePickerSheet
        visible={!!pick}
        onClose={() => setPick(null)}
        onPick={r => {
          if (pick) update({ entries: [...plan.entries, { day: pick.day, slot: pick.slot, recipeId: r.id }] });
          setPick(null);
        }}
      />
      <NutritionSheet
        visible={sheet === 'nutrition'}
        onClose={() => setSheet(null)}
        lines={perServingLines(plan.entries.map(e => lookup(e.recipeId)).filter((r): r is Recipe => !!r))}
        servings={plan.cycleDays}
        title={plan.title}
        caption={t('nutrition.perDayWeek')}
      />
      <Sheet visible={sheet === 'apply'} onClose={() => setSheet(null)} title={t('mealplan.applyToPlanner')}>
        <Caption>{t('planner.applyFrom')}</Caption>
        <Row>
          {Array.from({ length: 21 }, (_, i) => addDays(today, i)).map(d => (
            <Chip
              key={d}
              label={d === today ? t('common.today') : formatDay(d)}
              onPress={() => {
                addEntries(applyMealplan(plan, d));
                setSheet(null);
                notify(t('planner.applied'));
              }}
            />
          ))}
        </Row>
      </Sheet>
    </Screen>
  );
}
